/**
 * @voltxampere/web — `<vxa-erc-panel>` violations dialog (Sprint 56c)
 *
 * Floating dialog that lists every ERC violation surfaced by the
 * engine. Opens via topbar `<vxa-erc-badge>` click; closes via the
 * X button, backdrop click, or Escape.
 *
 * Per-violation row renders:
 *   - severity chip (error / warn / info)
 *   - localised rule title + body
 *   - meta context (component id, net name, voltage, …)
 *   - suggestion text (Sprint 55a Recovery Center hook)
 *   - "Locate component" action — switches to the simulator pane
 *     and seeds `selectedNodes` so the canvas focuses the offender
 *   - "Ignore" / "Restore" toggle per violation
 *
 * Ignored violations are hidden by default; a footer toggle reveals
 * them again.
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import { LitElement, css, html, nothing } from "lit";
import { customElement } from "lit/decorators.js";
import type { ERCViolation } from "@voltxampere/engine";

import { selectedNodes } from "../store/canvas.js";
import { setPane } from "../store/router.js";
import { t, type TranslationKey } from "../i18n/index.js";
import { SignalController } from "../lit-utils/signal-controller.js";
import {
  closeErcPanel,
  ercPanelOpen,
  ercPanelShowIgnored,
  ercReport,
  ercRunDurationMs,
  ignoreViolation,
  ignoredViolationKeys,
  unignoreViolation,
  violationKey,
  visibleViolationCounts,
  visibleViolations,
  clearIgnoredViolations,
} from "../store/erc.js";

const RULE_TITLE_KEY: Readonly<Record<ERCViolation["ruleId"], TranslationKey>> = {
  "floating-node": "erc.rule.floating-node.title",
  "missing-ground": "erc.rule.missing-ground.title",
  "unconnected-pin": "erc.rule.unconnected-pin.title",
  "short-circuit": "erc.rule.short-circuit.title",
  saturation: "erc.rule.saturation.title",
  overvoltage: "erc.rule.overvoltage.title",
  overheating: "erc.rule.overheating.title",
};

const RULE_BODY_KEY: Readonly<Record<ERCViolation["ruleId"], TranslationKey>> = {
  "floating-node": "erc.body.floating-node",
  "missing-ground": "erc.body.missing-ground",
  "unconnected-pin": "erc.body.unconnected-pin",
  "short-circuit": "erc.body.short-circuit",
  saturation: "erc.body.saturation",
  overvoltage: "erc.body.overvoltage",
  overheating: "erc.body.overheating",
};

const RULE_SUGGESTION_KEY: Readonly<Record<ERCViolation["ruleId"], TranslationKey>> = {
  "floating-node": "erc.suggestion.floating-node",
  "missing-ground": "erc.suggestion.missing-ground",
  "unconnected-pin": "erc.suggestion.unconnected-pin",
  "short-circuit": "erc.suggestion.short-circuit",
  saturation: "erc.suggestion.saturation",
  overvoltage: "erc.suggestion.overvoltage",
  overheating: "erc.suggestion.overheating",
};

const SEVERITY_LABEL_KEY: Readonly<Record<ERCViolation["severity"], TranslationKey>> = {
  error: "erc.panel.severity.error",
  warn: "erc.panel.severity.warn",
  info: "erc.panel.severity.info",
};

@customElement("vxa-erc-panel")
export class VxaErcPanel extends LitElement {
  private readonly _open = new SignalController(this, ercPanelOpen);
  private readonly _report = new SignalController(this, ercReport);
  private readonly _ignored = new SignalController(this, ignoredViolationKeys);
  private readonly _showIgnored = new SignalController(this, ercPanelShowIgnored);
  private readonly _duration = new SignalController(this, ercRunDurationMs);

  override connectedCallback(): void {
    super.connectedCallback();
    window.addEventListener("keydown", this._onKeydown);
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    window.removeEventListener("keydown", this._onKeydown);
  }

  private readonly _onKeydown = (e: KeyboardEvent): void => {
    if (e.key === "Escape" && this._open.value) {
      closeErcPanel();
    }
  };

  static override styles = css`
    :host {
      position: fixed;
      inset: 0;
      z-index: var(--z-modal);
      display: none;
      align-items: flex-start;
      justify-content: center;
      background: rgba(3, 5, 10, 0.7);
      backdrop-filter: blur(8px);
      -webkit-backdrop-filter: blur(8px);
    }

    :host([data-open]) {
      display: flex;
    }

    .panel {
      margin-top: 70px;
      width: min(720px, 92vw);
      max-height: 80vh;
      display: flex;
      flex-direction: column;
      background: var(--ink);
      border: 1px solid var(--ivory-wisp);
      box-shadow: 0 0 0 1px var(--brass-ghost);
    }

    .header {
      padding: var(--s-4) var(--s-5);
      border-bottom: 1px solid var(--ivory-wisp);
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: var(--s-4);
    }

    .titles {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }

    .heading {
      font-family: var(--font-display);
      font-size: var(--fs-h3);
      font-weight: 400;
      color: var(--ivory);
      letter-spacing: -0.01em;
    }

    .subheading {
      font-family: var(--font-mono);
      font-size: var(--fs-mono-tiny);
      letter-spacing: 0.22em;
      text-transform: uppercase;
      color: var(--ivory-faint);
    }

    .close {
      background: transparent;
      border: 1px solid var(--ivory-wisp);
      color: var(--ivory-dim);
      padding: 4px 8px;
      cursor: pointer;
      font-family: var(--font-mono);
      font-size: var(--fs-mono-tiny);
    }

    .close:hover {
      color: var(--ivory);
      border-color: var(--ivory);
    }

    .body {
      flex: 1;
      overflow-y: auto;
      padding: var(--s-4) var(--s-5);
    }

    .stats-row {
      display: flex;
      gap: var(--s-4);
      padding: 0 var(--s-5) var(--s-3);
      font-family: var(--font-mono);
      font-size: var(--fs-mono-tiny);
      letter-spacing: 0.18em;
      color: var(--ivory-faint);
      text-transform: uppercase;
    }

    .empty {
      padding: var(--s-6) var(--s-5);
      text-align: center;
      color: var(--ivory-dim);
      font-family: var(--font-body);
    }

    .empty .empty-title {
      font-family: var(--font-display);
      font-size: var(--fs-h3);
      color: var(--brass);
      margin-bottom: var(--s-3);
    }

    .violation {
      display: flex;
      gap: var(--s-4);
      padding: var(--s-4) 0;
      border-bottom: 1px solid var(--ivory-wisp);
    }

    .violation:last-child {
      border-bottom: none;
    }

    .severity-chip {
      flex: 0 0 80px;
      align-self: flex-start;
      padding: 2px 6px;
      font-family: var(--font-mono);
      font-size: var(--fs-mono-tiny);
      letter-spacing: 0.22em;
      text-transform: uppercase;
      text-align: center;
      border-radius: 2px;
    }

    .severity-chip[data-severity="error"] {
      color: var(--danger, #d9534f);
      border: 1px solid var(--danger, #d9534f);
    }

    .severity-chip[data-severity="warn"] {
      color: var(--brass-hot, #d4a857);
      border: 1px solid var(--brass-faint);
    }

    .severity-chip[data-severity="info"] {
      color: var(--ivory-dim);
      border: 1px solid var(--ivory-wisp);
    }

    .violation-body {
      flex: 1;
      display: flex;
      flex-direction: column;
      gap: 6px;
    }

    .violation-title {
      font-family: var(--font-display);
      font-size: var(--fs-body);
      color: var(--ivory);
    }

    .violation-message {
      font-family: var(--font-body);
      font-size: var(--fs-small);
      color: var(--ivory-dim);
    }

    .violation-meta {
      font-family: var(--font-mono);
      font-size: var(--fs-mono-tiny);
      letter-spacing: 0.12em;
      color: var(--ivory-faint);
    }

    .violation-suggestion {
      font-family: var(--font-body);
      font-size: var(--fs-small);
      color: var(--brass);
      font-style: italic;
    }

    .actions {
      display: flex;
      gap: var(--s-3);
      margin-top: 6px;
    }

    .action {
      background: transparent;
      border: 1px solid var(--ivory-wisp);
      color: var(--ivory-dim);
      padding: 3px 8px;
      cursor: pointer;
      font-family: var(--font-mono);
      font-size: var(--fs-mono-tiny);
      letter-spacing: 0.18em;
      text-transform: uppercase;
    }

    .action:hover {
      color: var(--brass);
      border-color: var(--brass-faint);
    }

    .footer {
      padding: var(--s-3) var(--s-5);
      border-top: 1px solid var(--ivory-wisp);
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: var(--s-3);
      font-family: var(--font-mono);
      font-size: var(--fs-mono-tiny);
      letter-spacing: 0.18em;
      color: var(--ivory-faint);
      text-transform: uppercase;
    }

    .footer-actions {
      display: flex;
      gap: var(--s-3);
    }
  `;

  override render() {
    this.toggleAttribute("data-open", this._open.value);
    if (!this._open.value) return nothing;
    const report = this._report.value;
    const counts = visibleViolationCounts(report);
    const visible = visibleViolations(report);
    const ignoredCount = report ? report.violations.length - visible.length : 0;
    const showIgnored = this._showIgnored.value;
    const ignoredList = report
      ? report.violations.filter((v) => this._ignored.value.has(violationKey(v)))
      : [];

    return html`
      <div class="panel" role="dialog" aria-modal="true" aria-label=${t("erc.panel.title")}>
        <div class="header">
          <div class="titles">
            <span class="heading">${t("erc.panel.title")}</span>
            <span class="subheading">${t("erc.panel.subtitle")}</span>
          </div>
          <button
            class="close"
            type="button"
            data-testid="erc-panel-close"
            @click=${closeErcPanel}
            aria-label=${t("erc.panel.close")}
          >
            ✕
          </button>
        </div>
        <div class="stats-row">
          ${this.renderDurationStat()} ${this.renderDcAwareStat(report?.dcAwareRulesRan ?? false)}
          ${counts.error > 0
            ? html`<span data-stat="error"
                >${t("erc.badge.summary.error", { count: String(counts.error) })}</span
              >`
            : nothing}
          ${counts.warn > 0
            ? html`<span data-stat="warn"
                >${t("erc.badge.summary.warn", { count: String(counts.warn) })}</span
              >`
            : nothing}
        </div>
        <div class="body" data-testid="erc-panel-body">
          ${visible.length === 0
            ? this.renderEmpty()
            : visible.map((v) => this.renderViolation(v, false))}
          ${showIgnored && ignoredList.length > 0
            ? html`<div data-section="ignored">
                ${ignoredList.map((v) => this.renderViolation(v, true))}
              </div>`
            : nothing}
        </div>
        <div class="footer">
          <span>
            ${ignoredCount > 0 ? t("erc.panel.ignored.count", { count: String(ignoredCount) }) : ""}
          </span>
          <div class="footer-actions">
            ${ignoredCount > 0
              ? html`
                  <button
                    class="action"
                    type="button"
                    data-testid="erc-panel-toggle-ignored"
                    @click=${this.toggleShowIgnored}
                  >
                    ${showIgnored
                      ? t("erc.panel.ignored.toggle.hide")
                      : t("erc.panel.ignored.toggle.show")}
                  </button>
                  <button
                    class="action"
                    type="button"
                    data-testid="erc-panel-clear-ignored"
                    @click=${clearIgnoredViolations}
                  >
                    ${t("erc.panel.ignored.clear")}
                  </button>
                `
              : nothing}
          </div>
        </div>
      </div>
    `;
  }

  private renderEmpty() {
    return html`
      <div class="empty" data-testid="erc-panel-empty">
        <div class="empty-title">${t("erc.panel.empty.title")}</div>
        <div>${t("erc.panel.empty.body")}</div>
      </div>
    `;
  }

  private renderDurationStat() {
    const ms = this._duration.value;
    if (ms === null) return nothing;
    return html`<span data-stat="duration"
      >${t("erc.panel.duration", { ms: ms.toFixed(1) })}</span
    >`;
  }

  private renderDcAwareStat(dcAware: boolean) {
    return html`<span data-stat="dc-aware"
      >${dcAware ? t("erc.panel.dc-aware") : t("erc.panel.dc-unaware")}</span
    >`;
  }

  private renderViolation(v: ERCViolation, isIgnored: boolean) {
    const componentId = typeof v.meta.componentId === "string" ? v.meta.componentId : null;
    const metaSummary = this.renderMeta(v);
    return html`
      <div class="violation" data-rule-id=${v.ruleId} data-testid="erc-violation">
        <span class="severity-chip" data-severity=${v.severity}>
          ${t(SEVERITY_LABEL_KEY[v.severity])}
        </span>
        <div class="violation-body">
          <span class="violation-title">${t(RULE_TITLE_KEY[v.ruleId])}</span>
          <span class="violation-message">${t(RULE_BODY_KEY[v.ruleId])}</span>
          ${metaSummary ? html`<span class="violation-meta">${metaSummary}</span>` : nothing}
          <span class="violation-suggestion">${t(RULE_SUGGESTION_KEY[v.ruleId])}</span>
          <div class="actions">
            ${componentId !== null
              ? html`<button
                  class="action"
                  type="button"
                  data-testid="erc-violation-locate"
                  @click=${(): void => {
                    this.locateComponent(componentId);
                  }}
                >
                  ${t("erc.panel.action.locate")}
                </button>`
              : nothing}
            ${isIgnored
              ? html`<button
                  class="action"
                  type="button"
                  data-testid="erc-violation-unignore"
                  @click=${(): void => {
                    unignoreViolation(v);
                  }}
                >
                  ${t("erc.panel.action.unignore")}
                </button>`
              : html`<button
                  class="action"
                  type="button"
                  data-testid="erc-violation-ignore"
                  @click=${(): void => {
                    ignoreViolation(v);
                  }}
                >
                  ${t("erc.panel.action.ignore")}
                </button>`}
          </div>
        </div>
      </div>
    `;
  }

  private renderMeta(v: ERCViolation): string {
    const parts: string[] = [];
    for (const [k, val] of Object.entries(v.meta)) {
      if (val === null || val === undefined) continue;
      let formatted: string;
      if (typeof val === "number") {
        formatted = val.toPrecision(4);
      } else if (typeof val === "string") {
        formatted = val;
      } else if (typeof val === "boolean") {
        formatted = val ? "true" : "false";
      } else {
        // Non-primitive meta values are out of scope for the inline
        // summary row — skip rather than render "[object Object]".
        continue;
      }
      parts.push(`${k}=${formatted}`);
    }
    return parts.join(" · ");
  }

  private readonly toggleShowIgnored = (): void => {
    ercPanelShowIgnored.value = !ercPanelShowIgnored.value;
  };

  private locateComponent(id: string): void {
    setPane("simulator");
    selectedNodes.value = [id];
    closeErcPanel();
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "vxa-erc-panel": VxaErcPanel;
  }
}
